import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Filter,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  Trash2,
  AlertTriangle,
  Clock,
  RefreshCw,
  Ban
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format DOB as MM/DD/YYYY
const formatDOB = (dateString) => {
  if (!dateString) return '-';
  // Handle YYYY-MM-DD format
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
  // Try parsing as date
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const ParticipantsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [recentParticipants, setRecentParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [activeSearch, setActiveSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [showingRecent, setShowingRecent] = useState(!searchParams.get('search'));
  const limit = 20;
  
  // Delete participant state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Check if user can delete participants
  const canDelete = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);

  // Get storage key for this user's recent participants
  const getRecentStorageKey = () => `myhelp_recent_participants_${user?.id}`;

  // Load recent participants from localStorage
  const loadRecentParticipants = useCallback(() => {
    if (!user?.id) return [];
    try {
      const stored = localStorage.getItem(getRecentStorageKey());
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [user?.id]);

  // Save participant to recent list
  const addToRecent = useCallback((participant) => {
    if (!user?.id || !participant?.id) return;
    
    const recent = loadRecentParticipants();
    // Remove if already exists
    const filtered = recent.filter(p => p.id !== participant.id);
    // Add to front with timestamp
    const updated = [
      { ...participant, viewedAt: new Date().toISOString() },
      ...filtered
    ].slice(0, 10); // Keep only last 10
    
    localStorage.setItem(getRecentStorageKey(), JSON.stringify(updated));
    setRecentParticipants(updated);
  }, [user?.id, loadRecentParticipants]);

  // Initialize search from URL params
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearch(urlSearch);
      setActiveSearch(urlSearch);
      setShowingRecent(false);
    }
  }, [searchParams]);

  // Load recent participants on mount
  useEffect(() => {
    if (user?.id) {
      const recent = loadRecentParticipants();
      setRecentParticipants(recent);
    }
  }, [user?.id, loadRecentParticipants]);

  // Fetch fresh data for recent participants
  const refreshRecentParticipants = async () => {
    const recent = loadRecentParticipants();
    if (recent.length === 0) {
      setParticipants([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    try {
      // Fetch fresh data for each recent participant
      const freshData = await Promise.all(
        recent.map(async (p) => {
          try {
            const response = await axios.get(`${API}/participants/${p.id}`);
            return { ...response.data, viewedAt: p.viewedAt };
          } catch {
            return p; // Keep old data if fetch fails
          }
        })
      );
      
      setParticipants(freshData);
      setTotal(freshData.length);
      // Update localStorage with fresh data
      localStorage.setItem(getRecentStorageKey(), JSON.stringify(freshData));
      setRecentParticipants(freshData);
    } catch (error) {
      console.error('Failed to refresh recent participants:', error);
    }
  };

  // Fetch participants (search mode)
  const fetchParticipants = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit.toString()
      });
      if (activeSearch) params.append('search', activeSearch);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/participants?${params}`);
      setParticipants(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (showingRecent) {
      const recent = loadRecentParticipants();
      if (recent.length > 0) {
        setParticipants(recent);
        setTotal(recent.length);
        setLoading(false);
        // Also refresh to get latest data in background
        refreshRecentParticipants();
      } else {
        setParticipants([]);
        setTotal(0);
        setLoading(false);
      }
    } else {
      fetchParticipants();
    }
  }, [showingRecent, page, statusFilter]);

  // Fetch when activeSearch changes (after user submits)
  useEffect(() => {
    if (!showingRecent && activeSearch !== undefined) {
      fetchParticipants();
    }
  }, [activeSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      setShowingRecent(false);
      setActiveSearch(search.trim());
      setPage(0);
    }
  };

  const clearSearch = () => {
    setSearch('');
    setActiveSearch('');
    setShowingRecent(true);
    setPage(0);
    const recent = loadRecentParticipants();
    setParticipants(recent);
    setTotal(recent.length);
  };

  const handleViewParticipant = (participant) => {
    addToRecent(participant);
    navigate(`/participants/${participant.id}`);
  };

  const handleManualRefresh = () => {
    if (showingRecent) {
      refreshRecentParticipants();
    } else {
      fetchParticipants();
    }
    toast.success('Data refreshed');
  };

  const openDeleteDialog = (participant, e) => {
    e.stopPropagation();
    setParticipantToDelete(participant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteParticipant = async () => {
    if (!participantToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/participants/${participantToDelete.id}`);
      toast.success(`Participant ${participantToDelete.full_name} has been deleted`);
      setDeleteDialogOpen(false);
      setParticipantToDelete(null);
      fetchParticipants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete participant');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="participants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Participants</h1>
          <p className="text-muted-foreground mt-1">
            Manage enrolled participants and their profiles
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleManualRefresh} title="Refresh data">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button asChild data-testid="new-participant-btn">
            <Link to="/participants/new">
              <Plus className="w-4 h-4 mr-2" />
              Add Participant
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search all participants by name, email, State ID, or last 4 SSN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="exited">Exited</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            {!showingRecent && (
              <Button type="button" variant="outline" onClick={clearSearch}>
                <Clock className="w-4 h-4 mr-2" />
                Show Recent
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="card-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">
                {showingRecent ? 'Recently Viewed' : 'Search Results'}
              </CardTitle>
              {showingRecent && (
                <Badge variant="secondary" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  Last 10
                </Badge>
              )}
            </div>
            <span className="text-sm text-muted-foreground">{total} {showingRecent ? 'recent' : 'found'}</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48" data-testid="loading-spinner">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : participants.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="participants-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">ID (SSN)</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">DOB</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Programs</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      {showingRecent && (
                        <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Viewed</th>
                      )}
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant) => (
                      <tr 
                        key={participant.id} 
                        className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleViewParticipant(participant)}
                        data-testid={`participant-row-${participant.id}`}
                      >
                        <td className="py-3 px-4 text-sm font-mono text-primary">
                          {participant.id}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{participant.full_name}</div>
                            {participant.is_banned && (
                              <Badge 
                                variant="destructive" 
                                className="bg-black text-white text-xs px-2 py-0.5"
                                data-testid={`banned-badge-${participant.id}`}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                BANNED
                              </Badge>
                            )}
                            {participant.out_of_service_area && (
                              <Badge 
                                variant="destructive" 
                                className="bg-red-600 text-white text-xs px-2 py-0.5"
                                data-testid={`out-of-service-badge-${participant.id}`}
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Out of Service Area
                              </Badge>
                            )}
                          </div>
                          {participant.email && (
                            <div className="text-sm text-muted-foreground">{participant.email}</div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">{formatDOB(participant.date_of_birth)}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {/* Use enrolled_programs which contains human-readable names */}
                            {participant.enrolled_programs?.slice(0, 2).map((program) => (
                              <Badge key={program} variant="secondary" className="text-xs">
                                {program}
                              </Badge>
                            ))}
                            {participant.enrolled_programs?.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{participant.enrolled_programs.length - 2}
                              </Badge>
                            )}
                            {(!participant.enrolled_programs || participant.enrolled_programs.length === 0) && (
                              <span className="text-xs text-muted-foreground">No programs</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={
                            participant.enrollment_status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                            participant.enrollment_status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                          }>
                            {participant.enrollment_status}
                          </Badge>
                        </td>
                        {showingRecent && (
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {participant.viewedAt 
                              ? formatDateTime(participant.viewedAt)
                              : '-'
                            }
                          </td>
                        )}
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewParticipant(participant);
                              }}
                              data-testid={`view-participant-${participant.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {canDelete && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => openDeleteDialog(participant, e)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-participant-${participant.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12" data-testid="no-results">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <h3 className="text-lg font-medium">
                {showingRecent ? 'No recently viewed participants' : 'No participants found'}
              </h3>
              <p className="text-muted-foreground mt-1">
                {showingRecent 
                  ? 'Search for participants to start building your recent list'
                  : 'Try adjusting your search or filters'
                }
              </p>
              <div className="flex justify-center gap-2 mt-4">
                {!showingRecent && (
                  <Button variant="outline" onClick={clearSearch}>
                    <Clock className="w-4 h-4 mr-2" />
                    Show Recent
                  </Button>
                )}
                <Button asChild>
                  <Link to="/participants/new">Add New Participant</Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Participant Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Participant
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete the participant and all their related data including enrollments, cases, notes, documents, and communications.
            </DialogDescription>
          </DialogHeader>
          
          {participantToDelete && (
            <div className="py-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{participantToDelete.full_name}</p>
                <p className="text-sm text-muted-foreground font-mono">{participantToDelete.id}</p>
                {participantToDelete.enrolled_programs?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Programs: {participantToDelete.enrolled_programs.join(', ')}
                  </p>
                )}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  Warning: This action cannot be undone!
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setParticipantToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteParticipant}
              disabled={deleting}
              data-testid="confirm-delete-participant-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Participant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParticipantsPage;
